import { Component, ReactNode } from 'react'
import * as styles from 'components/features/pfm/AccountsRates/AccountsRates.css'
import * as sumBy from 'lodash/sumBy'
import * as isNumber from 'lodash/isNumber'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { AccountType, AccountTyped, CategoryInterest, OptionalRateAccount, RateComparison as RateComparisonType } from 'interfaces/accounts'
import { CategoryId, OpportunityCategoryId } from 'interfaces/meta'
import { FooterCellDefinition } from 'interfaces/table'
import { TimeframeTyped } from 'interfaces/global'
import SavingsMonthly, { SavingsMonthlyProps } from 'components/unorganized/SavingsMonthly'
import AbsentValue from 'components/atoms/AbsentValue'
import Table, { TableColumnDefinition, TableComponentType } from 'components/molecules/tables/Table'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { isRatedAccount } from 'helpers/accountHelpers'
import Button from 'components/features/pfm/Button/Button'
import { ASSETS_RATES_EFFECTIVE_HINT, ASSETS_RATES_OPPORTUNITIES_HINTS, DEBTS_RATES_EFFECTIVE_HINT, DEBTS_RATES_OPPORTUNITIES_HINT } from 'constants/hints'
import { NO_INTEREST_CATEGORIES } from 'constants/accounts'
import { EmptyCardBlock } from 'components/features/pfm/Blocks/Blocks'
import NoAccountsCopy from 'components/molecules/NoAccountsCopy'
import RateComparisonComponent from 'components/unorganized/RateComparison'
import { OpportunityPlacement } from 'interfaces/opportunities'
import AccountBadge, { AccountBadgeProps } from 'components/features/pfm/AccountBadge/AccountBadge'
import AccountTooltip from 'components/features/pfm/AccountTooltip/AccountTooltip'
import RateTabsContainer from 'components/features/pfm/RateTabs/RateTabsContainer'
import { WithPlacement } from 'interfaces/insights'
import { RateTabContent } from 'components/features/pfm/RateTabs/RateTabContent'
import find from 'lodash/find'

export type AccountsRatesStateProps = {
  categoryInterests: CategoryInterest[]
  categoryAccounts: OptionalRateAccount[]
  selectedCategoryId: CategoryId
  opportunityCounters: Partial<Record<string, number>> | null
}

export type AccountsRatesDispatchProps = {
  setActiveTab: (active: CategoryId) => void
  onOpportunityLinkClick: (categoryId: OpportunityCategoryId) => void
}

export type AccountsRatesOwnProps =
  & AccountTyped
  & TimeframeTyped

export type AccountsRatesContainerOwnProps = WithPlacement<OpportunityPlacement>

export type AccountsRatesProps =
  & AccountsRatesStateProps
  & AccountsRatesDispatchProps
  & AccountsRatesOwnProps
  & AccountsRatesContainerOwnProps

type EmptyOrComponent<TProps> = (
  component: (props: any) => ReactNode,
  propsSelector: (props: TProps) => any,
  isFeesColumn?: boolean,
) => (props: TProps) => ReactNode

export type AccountsRatesRow = OptionalRateAccount

const CategoryInterestsTable = Table as TableComponentType<CategoryInterest>
const TypedTable = Table as TableComponentType<AccountsRatesRow>

// FOOTER DEFINITIONS
const ACCOUNT_SUMMARY_CELL: FooterCellDefinition<any, null> = {
  component: () => <span className={ styles.accountSummary }>Account Summary:</span>,
  propsReducer: () => null,
}

const EMPTY_CELL: FooterCellDefinition<AccountsRatesRow, null> = {
  component: () => null,
  propsReducer: () => null,
}

const createInterestPaidCell = (type: AccountType): FooterCellDefinition<OptionalRateAccount, number> => ({
  title: <span>Total <br />Interest { type === 'debts' ? 'Paid' : 'Earned' }</span>,
  component: formatTableCurrency,
  propsReducer: (props) =>
    sumBy(props, (account) =>
      isRatedAccount(account) && isNumber(account.rateObj.interest) ? account.rateObj.interest : 0),
})

const FEES_PAID_CELL: FooterCellDefinition<OptionalRateAccount, number> = {
  title: <span>Total <br />Fees Paid</span>,
  component: formatTableCurrency,
  propsReducer: (props) =>
    sumBy(props, (account) =>
      isRatedAccount(account) && isNumber(account.rateObj.fees_paid) ? account.rateObj.fees_paid : 0),
}

const OPPORTUNITY_CELL: FooterCellDefinition<OptionalRateAccount, number> = {
  title: <span>Total <br />Opportunities</span>,
  component: (totalOpportunities) => <SavingsMonthly savingsAmount={ totalOpportunities } />,
  propsReducer: (rows) => sumBy(rows, (account) =>
    isRatedAccount(account) && isNumber(account.rateObj.opportunity) ? account.rateObj.opportunity : 0),
}

const [DEBTS_FOOTER_DEFINITION, ASSETS_FOOTER_DEFINITION] = (() => {
  const firstColumns = [ACCOUNT_SUMMARY_CELL, EMPTY_CELL]
  const lastColumns = [FEES_PAID_CELL, OPPORTUNITY_CELL]

  return [
    [...firstColumns, createInterestPaidCell('debts'), ...lastColumns],
    [...firstColumns, createInterestPaidCell('assets'), ...lastColumns],
  ]
})()


const EMPTY_COLUMN = {
  name: '',
  title: '',
  propsSelector: (): null => null,
  component: () => <div />,
}

const createInterestPaidAllCell = (type: AccountType): FooterCellDefinition<CategoryInterest, number> => ({
  title: <span>Total <br />Interest { type === 'debts' ? 'Paid' : 'Earned' }</span>,
  component: formatTableCurrency,
  propsReducer: (props) =>
    sumBy(props, (row) => row.interest),
})

const FEES_PAID_ALL_CELL: FooterCellDefinition<CategoryInterest, number> = {
  title: <span>Total <br />Fees Paid</span>,
  component: formatTableCurrency,
  propsReducer: (props) => sumBy(props, (row) =>
    row.feesPaid),
}

type OpportunitiesCellAllProps = {
  totalAccounts: number
  totalOpportunities: number
}

const OPPORTUNITY_ALL_CELL: FooterCellDefinition<CategoryInterest, OpportunitiesCellAllProps> = {
  title: <span>Total <br />Opportunities</span>,
  component: (props) => (
    <div>{ props.totalOpportunities + '/' + props.totalAccounts }</div>
  ),
  propsReducer: (rows) => ({
    totalAccounts: sumBy(rows, (row) => row.allAccountsCount),
    totalOpportunities: sumBy(rows, (row) => row.opportunitiesCount),
  }),
}

const [DEBTS_FOOTER_ALL_DEFINITION, ASSETS_FOOTER_ALL_DEFINITION] = (() => {
  const firstColumns = [ACCOUNT_SUMMARY_CELL, EMPTY_CELL]
  const lastColumns = [FEES_PAID_ALL_CELL, OPPORTUNITY_ALL_CELL]

  return [
    [...firstColumns, createInterestPaidAllCell('debts'), ...lastColumns],
    [...firstColumns, createInterestPaidAllCell('assets'), ...lastColumns],
  ]
})()

class AccountsRates extends Component<AccountsRatesProps> {
  render () {
    const { type, categoryInterests, selectedCategoryId, placement } = this.props
    const activeTab = find(categoryInterests, { categoryId: selectedCategoryId })

    const onAllTab = selectedCategoryId === ALL_CATEGORY_ID

    return (
      <div className={ styles.accountRates }>
        <div className={ styles.tabs }>
          { this.renderTabs() }
        </div>
        <div className={ styles.tabContent }>
          {
            onAllTab
              ? this.renderAllTab()
              : this.renderCategoryTab()
          }
        </div>
        { !!activeTab && (
          <div>
            <RateTabContent
              placement={ placement }
              type={ type }
              activeTab={ activeTab }
            />
          </div>
        )}
      </div>
    )
  }

  private renderAllTab () {
    const { type, setActiveTab, categoryInterests } = this.props
    const onDebtsPage = type === 'debts'

    const emptyOrComponent: EmptyOrComponent<CategoryInterest> = (component, propsSelector, isFeesColumn) =>
      (props) => {
        const { feesPaid } = props
        const isEmpty = isFeesColumn && Number.isFinite(feesPaid) && feesPaid <= 0

        return isEmpty
          ? <AbsentValue> &ndash; </AbsentValue>
          : component(propsSelector(props))
      }

    const OpportunitiesCountButton = ({ opportunitiesCount, category }: CategoryInterest) =>
      opportunitiesCount > 0 ? (
        <div className={ styles.opportunitiesCountWrapper } onClick={ () => setActiveTab(category.id) }>
          <Button theme='danger' style='hollow' size='tiny' fullWidth>
            <span className={ styles.opportunitiesCountText }>{ opportunitiesCount } { opportunitiesCount > 1 ? 'issues' : 'issue' }</span>
          </Button>
        </div>
      ) : <div>None</div>

    const CategoryBadge = ({ category, allAccountsCount }: CategoryInterest) => (
      <div>
        <a className={ styles.categoryName } onClick={ () => setActiveTab(category.id) }>
          { category.value }
        </a>
        <div className={ styles.categoryCount }>{ allAccountsCount } { allAccountsCount > 1 ? 'Accounts' : 'Account' }</div>
      </div>
    )

    const ACCOUNT_COLUMN: TableColumnDefinition<CategoryInterest, CategoryInterest> = {
      name: 'accounts',
      title: 'Accounts',
      component: (props: CategoryInterest) =>
        <CategoryBadge { ...props } />,
    }

    const INTEREST_COLUMN: TableColumnDefinition<CategoryInterest, CategoryInterest> = {
      name: 'interest',
      title: onDebtsPage ? 'Interest Paid' : 'Interest Earned',
      responsiveTitle: true,
      component: emptyOrComponent(
        formatTableCurrency,
        (props: CategoryInterest) => props.interest,
      ),
    }

    const FEES_PAID_COLUMN: TableColumnDefinition<CategoryInterest, CategoryInterest> = {
      name: 'fees',
      title: 'Fees Paid',
      responsiveTitle: true,
      component: emptyOrComponent(
        formatTableCurrency,
        (props: CategoryInterest) => props.feesPaid,
        true,
      ),
    }

    const ISSUES_FOUND_COLUMN: TableColumnDefinition<CategoryInterest, CategoryInterest> = {
      name: 'opportunitiesCount',
      title: 'Issues Found',
      responsiveTitle: true,
      component: emptyOrComponent(
        OpportunitiesCountButton,
        (accountRates) => ({ ...accountRates, allowClickable: true }),
      ),
    }

    const columns: TableColumnDefinition<CategoryInterest, any>[] = [
      ACCOUNT_COLUMN,
      EMPTY_COLUMN,
      INTEREST_COLUMN,
      FEES_PAID_COLUMN,
      ISSUES_FOUND_COLUMN,
    ]

    const footer = onDebtsPage ? DEBTS_FOOTER_ALL_DEFINITION : ASSETS_FOOTER_ALL_DEFINITION

    const categoryInterestsWoAll = categoryInterests
      .filter((categoryInterest) => categoryInterest.category.id !== ALL_CATEGORY_ID && categoryInterest.allAccountsCount > 0)

    return (
      <CategoryInterestsTable
        rows={ categoryInterestsWoAll }
        columns={ columns }
        footer={ footer }
        isHeaderWhite
        className={ styles.allRatesTable }
      />
    )
  }

  private renderCategoryTab () {
    const {
      type,
      selectedCategoryId,
      categoryAccounts,
      opportunityCounters,
      onOpportunityLinkClick,
    } = this.props

    const onDebtsPage = type === 'debts'

    const categoryOpportunitiesCount = opportunityCounters ? opportunityCounters[selectedCategoryId] : null

    const emptyOrComponent: EmptyOrComponent<OptionalRateAccount> = (component, propsSelector) =>
      (props) => {
        const selectedProps = propsSelector(props)

        return selectedProps === null
          ? <AbsentValue>N/A</AbsentValue>
          : component(propsSelector(props))
      }

    const ACCOUNT_COLUMN: TableColumnDefinition<OptionalRateAccount, OptionalRateAccount> = {
      name: 'accounts',
      title: 'Accounts',
      component: (props: OptionalRateAccount) => {

        const accountBadgeProps: AccountBadgeProps = {
          name: props.name,
          cardNumber: props.number,
          cardType: props.card_type,
          updatedAt: props.updated_at,
          tooltipChild: (
            <AccountTooltip type={ type } account={ props } />
          ),
        }

        return (
          <AccountBadge { ...accountBadgeProps } />
        )
      },
    }

    const RATES_COLUMN: TableColumnDefinition<OptionalRateAccount, OptionalRateAccount> = {
      name: 'rates',
      title: (
        <span>
          Effective Rate <br className={ styles.hideForSmall } />vs. Peer Range
        </span>
      ),
      responsiveTitle: true,
      helpText: onDebtsPage ? DEBTS_RATES_EFFECTIVE_HINT : ASSETS_RATES_EFFECTIVE_HINT,
      component: emptyOrComponent(
        (props: RateComparisonType) => (
          <RateComparisonComponent { ...props } type={ type } />
        ),
        (props: OptionalRateAccount): RateComparisonType | null =>
          isRatedAccount(props)
            ? {
              effective_rate: props.rateObj.effective_rate,
              overall_bounds: props.rateObj.overall_bounds,
              fair_bounds: props.rateObj.fair_bounds,
            } : null,
      ),
    }

    const INTEREST_COLUMN: TableColumnDefinition<OptionalRateAccount, number | null> = {
      name: 'interest',
      title: onDebtsPage ? 'Interest Paid' : 'Interest Earned',
      responsiveTitle: true,
      component: formatTableCurrency,
      propsSelector: (props: OptionalRateAccount) => isRatedAccount(props) ? props.rateObj.interest : null,
    }

    const FEES_PAID_COLUMN: TableColumnDefinition<OptionalRateAccount, OptionalRateAccount> = {
      name: 'fees',
      title: 'Fees Paid',
      responsiveTitle: true,
      component: emptyOrComponent(
        formatTableCurrency,
        (props: OptionalRateAccount) => isRatedAccount(props) ? props.rateObj.fees_paid : null,
      ),
    }

    const opportunityHelpText = onDebtsPage
      ? DEBTS_RATES_OPPORTUNITIES_HINT
      : ASSETS_RATES_OPPORTUNITIES_HINTS[selectedCategoryId]

    const OPPORTUNITY_COLUMN: TableColumnDefinition<OptionalRateAccount, SavingsMonthlyProps> = {
      name: 'opportunitiesCount',
      title: (
        <span>
          { onDebtsPage ? 'Potential Savings' : 'Potential Earnings' }
        </span>
      ),
      responsiveTitle: true,
      helpText: opportunityHelpText,
      component: SavingsMonthly,
      propsSelector: (props) => {
        const savingsAmount = isRatedAccount(props) ? props.rateObj.opportunity : null

        return {
          savingsAmount,
          onClick: categoryOpportunitiesCount && savingsAmount // both cases considered: `0` and `null | undefined`
            ? () => onOpportunityLinkClick(selectedCategoryId as OpportunityCategoryId)
            : undefined,
        }
      },
    }

    const columns: TableColumnDefinition<AccountsRatesRow, any>[] = [
      ACCOUNT_COLUMN,
      RATES_COLUMN,
      INTEREST_COLUMN,
      FEES_PAID_COLUMN,
      OPPORTUNITY_COLUMN,
    ]

    const footer = type === 'debts' ? DEBTS_FOOTER_DEFINITION : ASSETS_FOOTER_DEFINITION

    return (
      <TypedTable
        rows={ categoryAccounts }
        columns={ columns }
        footer={ footer }
        isHeaderWhite
        className={ styles.tabbedRatesTable }
      />
    )
  }

  private renderTabs () {
    const { type, categoryInterests, selectedCategoryId, setActiveTab } = this.props

    let tabsComponent = (
      <RateTabsContainer
        tabs={ categoryInterests }
        active={ selectedCategoryId }
        type={ type }
        setActiveTab={ setActiveTab }
      />
    )

    if (NO_INTEREST_CATEGORIES.has(selectedCategoryId)) {
      tabsComponent = (
        <EmptyCardBlock>
          <NoAccountsCopy type={ type } categoryId={ selectedCategoryId } standalone />
        </EmptyCardBlock>
      )
    }

    return tabsComponent
  }
}

export default AccountsRates
